import { useQuery } from '@apollo/client'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@dfh-corp-sites/ui'
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'

import { SanityCta, SanityParentProductFragment } from '../../../graphql/gatsby'
import {
  ConfigurableProductDetailsFragment,
  GetProductsByUrlKeyDocument,
  GetProductsByUrlKeyQuery,
  SimpleProductDetailsFragment,
} from '../../../graphql/magento'
import { ProductLabelStatus, SearchParentProductBySkuDocument } from '../../../graphql/search'
import useIsTruemedAvailable from '../../../hooks/useIsTruemedAvailable'
import { useLabelName } from '../../../hooks/useLabelName'
import { usePreviousPath } from '../../../hooks/usePreviousPath'
import { useRefetchOnChange } from '../../../hooks/useRefetchOnChange'
import useRetailPrices from '../../../hooks/useRetailPrices'
import { useSiteSettingsAndMetadata } from '../../../hooks/useSiteSettingsAndMetadata'
import { TokenContext } from '../../../layouts/context'
import { setAnalyticsEvent } from '../../../lib/analytics/state'
import { useDispensaryContext } from '../../../lib/dispensaries'
import { isSanityImageWithAssetId } from '../../../lib/products/images'
import { updateUrlHashForSku } from '../../../lib/products/utils/pdp'
import { useSearchLazyQuery } from '../../../lib/search'
import { imageUrlFor } from '../../../utils/image-url'
import Link from '../../global/Link'
import TopBanner from '../../sanity-page/TopBanner'
import Spinner from '../../Spinner'
import ItemDetails from './item_details/ItemDetails'
import RelatedProductsCarousel from './RelatedProductsCarousel/RelatedProductsCarousel'

export interface ItemContainerProps {
  className?: string
  pageContext?: {
    urlKey: string
  }
  sanityParentProduct?: SanityParentProductFragment | null
  sanityChildProducts?: SanityParentProductFragment['products'] | null
}

export const takeIfConfigurableProduct = (
  item: NonNullable<NonNullable<GetProductsByUrlKeyQuery['products']>['items']>[number] | undefined,
): ConfigurableProductDetailsFragment | undefined =>
  item?.__typename === 'ConfigurableProduct' ? item : undefined

const ItemContainer: FC<ItemContainerProps> = ({
  pageContext = {
    urlKey: '',
  },
  className = '',
  sanityParentProduct,
  sanityChildProducts,
}) => {
  const token = useContext(TokenContext)
  const { shouldDisplayRetailPrice } = useRetailPrices()

  const [selectedVariant, setSelectedVariant] = useState<
    SimpleProductDetailsFragment | null | undefined
  >(undefined)
  const [selectedChild, setSelectedChild] = useState<
    NonNullable<SanityParentProductFragment['products']>[number] | null | undefined
  >(undefined)
  const { data, networkStatus, refetch } = useQuery(GetProductsByUrlKeyDocument, {
    fetchPolicy: 'cache-and-network',
    context: { token },
    variables: { urlKey: pageContext.urlKey },
  })
  const dispensary = useDispensaryContext()
  const labelNameResult = useLabelName()
  const [executeParentProductBySkuSearch, { data: lazyData, called: lazyCalled }] =
    useSearchLazyQuery(SearchParentProductBySkuDocument, {
      notifyOnNetworkStatusChange: true,
    })

  useRefetchOnChange({ networkStatus, refetch }, [token])

  useEffect(() => {
    if (!selectedVariant || selectedVariant.__typename !== 'SimpleProduct') {
      return
    }
    const finalPrice = selectedVariant.price_tiers?.[0]?.final_price
    const currency = finalPrice?.currency
    const price = finalPrice?.value
    if (currency == null || price == null) {
      return
    }
    setAnalyticsEvent({
      event: 'view_item',
      currency,
      value: price,
      items: [
        {
          item_id: selectedVariant.sku || undefined,
          item_name: selectedVariant.name as string,
          currency,
          price,
        },
      ],
      app_context: dispensary ? 'dispensary_pdp' : 'pdp',
    })
  }, [selectedVariant, dispensary])

  const item = takeIfConfigurableProduct(data?.products?.items?.[0])

  const onVariantSelected = useCallback(
    (itemValue: string) => {
      updateUrlHashForSku(itemValue || null)
      if (itemValue) {
        setSelectedVariant(item?.variants?.find((ver) => ver?.product?.sku === itemValue)?.product)
        setSelectedChild(sanityChildProducts?.find((child) => child?.sku === itemValue))
      } else {
        setSelectedVariant(undefined)
      }
    },
    [item, sanityChildProducts],
  )

  const selectedVariantLabelStatus = useMemo(
    () =>
      selectedVariant?.sku
        ? (lazyData?.parentProductBySku?.products.edges.find(
            ({ node }) => node && node.sku === selectedVariant.sku,
          )?.node.labelStatus as ProductLabelStatus)
        : undefined,
    [selectedVariant?.sku, lazyData?.parentProductBySku?.products.edges],
  )

  const selectedVariantRetailPrice = useMemo(
    () =>
      selectedVariant?.sku && shouldDisplayRetailPrice
        ? lazyData?.parentProductBySku?.products.edges.find(
            ({ node }) => node && node.sku === selectedVariant.sku,
          )?.node.retailPrice
        : undefined,
    [selectedVariant?.sku, lazyData?.parentProductBySku?.products.edges, shouldDisplayRetailPrice],
  )

  // Conditionally search parent product by sku for label statuses
  useEffect(() => {
    if (!item) {
      return
    }

    const exactVariantSkus =
      item.variants?.reduce((acc: string[], variant) => {
        const variantProductSku = variant?.product?.sku
        if (variantProductSku) {
          acc.push(variantProductSku)
        }
        return acc
      }, []) || []

    // Verify there are variants to check label statuses
    if (
      labelNameResult._tag === 'Success' &&
      item.sku &&
      exactVariantSkus.length > 0 &&
      !lazyCalled
    ) {
      executeParentProductBySkuSearch({
        variables: {
          sku: item.sku,
          labelName: labelNameResult.labelName,
          includeLabel: true,
        },
      })
    } else if (
      labelNameResult._tag !== 'Success' &&
      item.sku &&
      exactVariantSkus.length > 0 &&
      !lazyCalled
    ) {
      // request parent product since we need retail price for each child product
      executeParentProductBySkuSearch({
        variables: {
          sku: item.sku,
          labelName: '',
          includeLabel: false,
        },
      })
    }
  }, [executeParentProductBySkuSearch, item, labelNameResult, lazyCalled])

  const selectedSanityProduct = selectedVariant
    ? sanityParentProduct?.products?.find((product) => product?.sku === selectedVariant.sku)
    : undefined
  const selectedVariantPrice = selectedSanityProduct?.prices?.retail?.amount ?? null
  const selectedVariantCurrency = selectedSanityProduct?.prices?.retail?.currency ?? null
  const selectedVariantImage =
    selectedSanityProduct && isSanityImageWithAssetId(selectedSanityProduct.mainImage)
      ? imageUrlFor(selectedSanityProduct.mainImage).width(1200).auto('format').url()
      : null

  const { site, sanitySiteSettings } = useSiteSettingsAndMetadata() || {}
  const productionSiteBaseUrl = site?.siteMetadata?.productionSiteBaseUrl
  const previousPath = usePreviousPath()

  const pdpTopBanner = sanitySiteSettings?.pdpTopBanner

  const relatedProductSkus = useMemo(
    () =>
      sanityParentProduct?.relatedProducts?.reduce((acc: string[], relatedProduct) => {
        const sku = relatedProduct?.sku
        if (sku) {
          acc.push(sku)
        }
        return acc
      }, []) ?? [],
    [sanityParentProduct],
  )

  const isTruemedAvailable = useIsTruemedAvailable({
    skus: selectedVariant?.sku ? [selectedVariant?.sku] : [],
  })

  if (item) {
    return (
      <>
        {item.name && (
          <Helmet>
            <title>{item.name}</title>
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org/',
                '@type': 'Product',
                brand: sanityParentProduct?.brand,
                name: item.name,
                image: selectedVariantImage,
                sku: selectedVariant?.sku,
                description: sanityParentProduct?.metaDescription,
                offers: {
                  '@type': 'Offer',
                  url: `${productionSiteBaseUrl}/products/${pageContext.urlKey}/#${selectedVariant?.sku}`,
                  priceCurrency: selectedVariantCurrency,
                  price: selectedVariantPrice,
                  itemCondition: 'https://schema.org/NewCondition',
                  availability: 'https://schema.org/InStock',
                },
              })}
            </script>
          </Helmet>
        )}

        {isTruemedAvailable && (
          <TopBanner text={pdpTopBanner?.text} cta={pdpTopBanner?.cta as SanityCta} />
        )}
        <div className={className}>
          <section
            className={`${!sanityParentProduct?.relatedProducts?.length ? 'mb-3.5 md:mb-6' : ''}`}
          >
            <Breadcrumb className="md:w-1/2 px-2.5 md:px-5 xl:px-10 pt-3.5 md:pt-6 md:border-r md:border-brand-rule-grey text-sm xl:text-base">
              <BreadcrumbList>
                <BreadcrumbItem>
                  {previousPath ? (
                    <BreadcrumbLink asChild>
                      <Link to={previousPath.href}>{previousPath.title}</Link>
                    </BreadcrumbLink>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link to="/products" aria-label="Breadcrumb Link to Shop Page">
                        Shop
                      </Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{item.name}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <ItemDetails
              item={item}
              sanityParentProduct={sanityParentProduct}
              sanityChildProduct={selectedChild}
              selectedVariant={selectedVariant}
              onVariantSelected={onVariantSelected}
              variantLabelStatus={selectedVariantLabelStatus}
              variantRetailPrice={selectedVariantRetailPrice}
            />
          </section>
          {sanityParentProduct?.relatedProducts &&
            sanityParentProduct.relatedProducts.length > 0 && (
              <RelatedProductsCarousel
                carouselTitle="Related Products"
                parentProductName={item.name || ''}
                relatedProductSkus={relatedProductSkus}
              />
            )}
        </div>
      </>
    )
  }

  return (
    <div style={{ background: '#fff', padding: '3rem' }}>
      <Spinner loading />
    </div>
  )
}

export default ItemContainer
